import { getPharmacySettings } from "@/core/admin/establishment-store";

export const HOSPITOL_UI_PERMISSIONS = {
  access: ["hospitol.access"],
  dashboard: ["hospitol.dashboard.read"],
  admissions: ["hospitol.admissions.read"],
  admissionsWrite: ["hospitol.admissions.write"],
  patients: ["hospitol.patients.read"],
  patientsWrite: ["hospitol.patients.write"],
  clinical: ["hospitol.clinical.read"],
  pharmacy: ["hospitol.pharmacy.read", "hospitol.pharmacy.invoice"],
  pharmacyValidate: ["hospitol.pharmacy.validate"],
  laboratory: ["hospitol.laboratory.read"],
  imaging: ["hospitol.imaging.read"],
  rh: ["hospitol.rh.read"],
  stock: ["hospitol.stock.read"],
  billing: ["hospitol.billing.read"],
  insurance: ["hospitol.insurance.read", "hospitol.admin.read", "iam.user.manage"],
  insuranceManage: ["hospitol.insurance.manage", "hospitol.admin.read", "iam.user.manage"],
  insuranceValidate: ["hospitol.insurance.validate", "hospitol.admin.read", "iam.user.manage"],
  insurancePortal: ["hospitol.insurance.portal"],
  nursing: ["hospitol.nursing.read"],
  nursingVitals: ["hospitol.nursing.vitals.write"],
  hospitalization: ["hospitol.hospitalization.read"],
  bedsAssign: ["hospitol.beds.assign"],
  admin: ["hospitol.admin.read", "hospitol.admin.write"],
} as const;

/** @deprecated Utiliser HOSPITOL_UI_PERMISSIONS */
export const PHARMACY_UI_PERMISSIONS = HOSPITOL_UI_PERMISSIONS;

export function canAccessHospitol(auth: { hasAnyPermission: (keys: string[]) => boolean }) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.access]);
}

export function canAccessDashboardSection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.dashboard]);
}

export function canAccessFinancialDashboard(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([
    ...HOSPITOL_UI_PERMISSIONS.dashboard,
    ...HOSPITOL_UI_PERMISSIONS.billing,
  ]);
}

export function canAccessOperationalDashboard(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([
    ...HOSPITOL_UI_PERMISSIONS.dashboard,
    ...HOSPITOL_UI_PERMISSIONS.admissions,
  ]);
}

export function canAccessPharmacyModule(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.pharmacy]);
}

export function canAccessRhSection(auth: { hasAnyPermission: (keys: string[]) => boolean }) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.rh]);
}

export function canAccessStockSection(auth: { hasAnyPermission: (keys: string[]) => boolean }) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.stock]);
}

export function canAccessBillingSection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.billing]);
}

export function canAccessPatientsSection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.patients]);
}

/** Création, modification et suppression de dossiers patients. */
export function canWritePatients(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([
    ...HOSPITOL_UI_PERMISSIONS.patientsWrite,
    ...HOSPITOL_UI_PERMISSIONS.admin,
  ]);
}

export function canAccessAdmissionsSection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.admissions]);
}

/** Enregistrement, prise en charge et orientation des admissions / urgences. */
export function canWriteAdmissions(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([
    ...HOSPITOL_UI_PERMISSIONS.admissionsWrite,
    ...HOSPITOL_UI_PERMISSIONS.admin,
  ]);
}

export function canAccessClinicalSection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.clinical]);
}

export function canAccessLaboratorySection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.laboratory]);
}

export function canAccessImagingSection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.imaging]);
}

export function canValidatePharmacyPrescription(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.pharmacyValidate]);
}

/** Ordonnance saisie directement en officine (paramètre admin, désactivé par défaut). */
export function canCreatePharmacistDirectPrescription(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}): boolean {
  if (!getPharmacySettings().allowPharmacistDirectPrescription) return false;
  return canValidatePharmacyPrescription(auth);
}

export function canAccessAdminSection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.admin]);
}

export function canAccessInsuranceSection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.insurance]);
}

export function canManageInsurance(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.insuranceManage]);
}

export function canValidateInsuranceCase(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.insuranceValidate]);
}

export function canAccessInsurancePortal(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([...HOSPITOL_UI_PERMISSIONS.insurancePortal]);
}

/** Infirmier(ère) — lecture soins & constantes (pas clinical.read seul). */
export function canAccessNursingSection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([
    ...HOSPITOL_UI_PERMISSIONS.nursing,
    ...HOSPITOL_UI_PERMISSIONS.nursingVitals,
  ]);
}

/** Saisie des constantes vitales (infirmier). */
export function canWriteNursingVitals(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([
    ...HOSPITOL_UI_PERMISSIONS.nursingVitals,
    ...HOSPITOL_UI_PERMISSIONS.admin,
  ]);
}

/** Validation médicale des constantes. */
export function canValidateNursingVitals(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([
    ...HOSPITOL_UI_PERMISSIONS.nursingVitals,
    ...HOSPITOL_UI_PERMISSIONS.admin,
  ]);
}

export function canAccessHospitalizationSection(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([
    ...HOSPITOL_UI_PERMISSIONS.hospitalization,
    ...HOSPITOL_UI_PERMISSIONS.bedsAssign,
  ]);
}

export function canAssignBeds(auth: {
  hasAnyPermission: (keys: string[]) => boolean;
}) {
  return auth.hasAnyPermission([
    ...HOSPITOL_UI_PERMISSIONS.bedsAssign,
    ...HOSPITOL_UI_PERMISSIONS.hospitalization,
    ...HOSPITOL_UI_PERMISSIONS.admin,
  ]);
}
